/** @format */

import PropTypes from "prop-types";
import { useState } from "react";

// material-ui
import { styled } from "@mui/material/styles";
import {
	Avatar,
	Box,
	Button,
	createTheme,
	Grid,
	Typography,
} from "@mui/material";

// third-party
import Chart from "react-apexcharts";

// project imports
import MainCard from "../cards/MainCard";
import SkeletonTotalCurrentContributionCard from "../cards/skeleton/SkeletonTotalCurrentContributionCard";

import ChartDataMonth from "../chart-data/TotalCurrentContributionCardMonthChart";
import ChartDataYear from "../chart-data/TotalCurrentContributionCardYearChart";

// assets
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";

declare module "@mui/material/styles" {
	interface PaletteColor {
		800?: string;
	}
	interface SimplePaletteColorOptions {
		800?: string;
	}

	interface TypographyVariants {
		commonAvatar: React.CSSProperties;
		smallAvatar: React.CSSProperties;
		mediumAvatar: React.CSSProperties;
		largeAvatar: React.CSSProperties;
	}

	// Allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		commonAvatar?: React.CSSProperties;
		smallAvatar?: React.CSSProperties;
		mediumAvatar: React.CSSProperties;

		largeAvatar?: React.CSSProperties;
	}
}

const theme = createTheme({
	palette: {
		secondary: {
			main: "#f50057",
			dark: "#c51162",
			800: "#9c0045", // Your custom numeric shade
		},
	},
});

const CardWrapper = styled(MainCard)(({ theme }) => ({
	backgroundColor: colors.primary,
	color: "#fff",
	overflow: "hidden",
	position: "relative",
	"&>div": {
		position: "relative",
		zIndex: 5,
	},
	"&:after": {
		content: '""',
		position: "absolute",
		width: 210,
		height: 210,
		background: colors.primary,
		borderRadius: "50%",
		zIndex: 1,
		top: -85,
		right: -95,
		[theme.breakpoints.down("sm")]: {
			top: -105,
			right: -140,
		},
	},
	"&:before": {
		content: '""',
		position: "absolute",
		zIndex: 1,
		width: 210,
		height: 210,
		background: colors.primary,
		borderRadius: "50%",
		top: -125,
		right: -15,
		opacity: 0.5,
		[theme.breakpoints.down("sm")]: {
			top: -155,
			right: -70,
		},
	},
}));

export interface TotalCurrentContributionCardProps {
	isLoading: boolean;
	totalCurrentCycleContributionAmount: string;
	currency?: string;
}

const TotalCurrentContributionCard = (
	props: TotalCurrentContributionCardProps,
) => {
	const { isLoading, totalCurrentCycleContributionAmount, currency = "usd" } = props;

	const [timeValue, setTimeValue] = useState(false);

	// Safari browser detection
	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	// const handleChangeTime = (event: any, newValue: any) => {
	//   setTimeValue(newValue);
	// };

	return (
		<>
			{isLoading ?
				<SkeletonTotalCurrentContributionCard />
				: <CardWrapper border={false} content={false}>
					<Box sx={{ p: 2.25 }}>
						<Grid container direction="column">
							<Grid item>
								<Grid container justifyContent="space-between">
									<Grid item>
										<Avatar
											variant="rounded"
											sx={{
												...theme.typography.commonAvatar,
												...theme.typography.largeAvatar,
												backgroundColor: colors.mainSecondary,
												color: "#fff",
												mt: 1,
											}}>
											<LocalMallOutlinedIcon fontSize="inherit" />
										</Avatar>
									</Grid>
									{/* <Grid item>
                    <Button
                      disableElevation
                      variant={timeValue ? "contained" : "text"}
                      size="small"
                      sx={{ color: "inherit" }}
                      onClick={(e) => handleChangeTime(e, true)}
                    >
                      Month
                    </Button>
                    <Button
                      disableElevation
                      variant={!timeValue ? "contained" : "text"}
                      size="small"
                      sx={{ color: "inherit" }}
                      onClick={(e) => handleChangeTime(e, false)}
                    >
                      Year
                    </Button>
                  </Grid> */}
								</Grid>
							</Grid>
							<Grid item sx={{ mb: 0.75 }}>
								<Grid container alignItems="center">
									<Grid item xs={6}>
										<Grid container alignItems="center">
											<Grid item>
												{timeValue ?
													<Typography
														sx={{
															fontSize: "2.125rem",
															fontWeight: 400,
															mr: 1,
															mt: 1.75,
															mb: 0.75,
														}}>
														{formatCurrency(108, currency)}
													</Typography>
													: <Typography
														sx={{
															fontSize: "2.125rem",
															fontWeight: 400,
															mr: 1,
															mt: 1.75,
															mb: 0.75,
														}}>
														{formatCurrency(Number(totalCurrentCycleContributionAmount), currency)}
													</Typography>
												}
											</Grid>

											<Grid item xs={12}>
												<Typography
													sx={{
														fontSize: "1.5rem",
														fontWeight: 500,
														color: colors.white,
													}}>
													Current Contribution
												</Typography>
											</Grid>
										</Grid>
									</Grid>
									{/* <Grid item xs={6}>
                    {timeValue ? (
                      <Chart
                        key={
                          isSafari
                            ? `safari-month-chart-${Date.now()}`
                            : "month-chart"
                        }
                        {...ChartDataMonth}
                      />
                    ) : (
                      <Chart
                        key={
                          isSafari
                            ? `safari-year-chart-${Date.now()}`
                            : "year-chart"
                        }
                        {...ChartDataYear}
                      />
                    )}
                  </Grid> */}
								</Grid>
							</Grid>
						</Grid>
					</Box>
				</CardWrapper>
			}
		</>
	);
};

TotalCurrentContributionCard.propTypes = {
	isLoading: PropTypes.bool,
	totalCurrentCycleContributionAmount: PropTypes.string,
};

export default TotalCurrentContributionCard;
